/*
 * Renders a group of checkboxes that can be used to select
 * multiple values from a set of options.
 *
 * The corresponding component when rendering the selected
 * values is PropertyGroup.
 *
 */

import React, { useState } from 'react';
import { arrayOf, bool, node, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FieldArray } from 'react-final-form-arrays';
import { FieldCheckbox, ValidationError } from '../../components';
import { ReactComponent as ArrowIcon } from './Arrow.svg';

import css from './FieldCheckboxGroup.module.css';
import FieldSubDescription from '../FieldSubDescription/FieldSubDescription';

const FieldCheckboxRenderer = props => {
  const {
    className,
    rootClassName,
    label,
    subDescription,
    optionLabelClassName,
    twoColumns,
    id,
    fields,
    options,
    meta,
    isCollapsible,
  } = props;

  const [isExpanded, setIsExpanded] = useState(isCollapsible ? false : true);

  const classes = classNames(rootClassName || css.root, className);
  const listClass = isCollapsible ? css.collapsibleList : css.list;
  const listClasses = twoColumns ? classNames(listClass, css.twoColumns) : listClass;

  const optionsToRender = isExpanded ? options : options.slice(0, 2);

  return (
    <fieldset className={classes}>
      {label ? <legend className={css.labelSpacing}>{label}</legend> : null}
      <FieldSubDescription subDescription={subDescription} />
      <ul
        className={listClasses}
        style={isCollapsible && isExpanded ? {background: 'unset'} : {}}
      >
        {isCollapsible ? <div
          className={`${css.collapsibleWrapper} ${isExpanded ? css.expandedCollapsibleWrapper : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <ArrowIcon className={isExpanded ? "" : css.flippedArrowIcon} />
        </div> : null}
        {optionsToRender.map((option, index) => {
          const fieldId = `${id}.${option.key}`;
          const textClassName = optionLabelClassName;
          const textClassNameMaybe = textClassName ? { textClassName } : {};
          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox
                id={fieldId}
                name={fields.name}
                label={option.label}
                value={option.key}
                {...textClassNameMaybe}
              />
            </li>
          );
        })}
      </ul>
      <ValidationError fieldMeta={{ ...meta }} />
    </fieldset>
  );
};

FieldCheckboxRenderer.defaultProps = {
  rootClassName: null,
  className: null,
  label: null,
  twoColumns: false,
};

FieldCheckboxRenderer.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  label: node,
  options: arrayOf(
    shape({
      key: string.isRequired,
      label: node.isRequired,
    })
  ).isRequired,
  twoColumns: bool,
  subDescription: string,
};

const FieldCheckboxGroup = props => <FieldArray component={FieldCheckboxRenderer} {...props} />;

// Name and component are required fields for FieldArray.
// Component-prop we define in this file, name needs to be passed in
FieldCheckboxGroup.propTypes = {
  name: string.isRequired,
};

export default FieldCheckboxGroup;
